/**
 * Navigation UI Manager - Handles navigation chips, campus display, and materials counting
 * Part of the SOLID Course Materials System
 *
 * @author Course Materials Team
 * @version 1.0.0
 */

class NavigationUIManager {
  /**
   * @param {Object} config - Configuration object
   * @param {Array<any>} config.selectedFilters - Selected filters array
   * @param {Array<any>} config.products - Products array
   */
  constructor(config = /** @type {any} */ ({})) {
    /** @type {Array<any>} */
    this.selectedFilters = config.selectedFilters || [];
    /** @type {Array<any>} */
    this.products = config.products || [];

    // Material type filter state
    this.currentMaterialTypeFilter = 'all';
    this.dropdownOpen = false;

    // Bind methods to preserve context
    this.initializeDepartmentFilters =
      this.initializeDepartmentFilters.bind(this);
    this.generateDepartmentChips = this.generateDepartmentChips.bind(this);
    this.filterProductsByDepartment =
      this.filterProductsByDepartment.bind(this);
    this.updateMaterialsCount = this.updateMaterialsCount.bind(this);
    this.updateCampusTermDisplay = this.updateCampusTermDisplay.bind(this);
    this.hideNavigationPanels = this.hideNavigationPanels.bind(this);
    this.refreshNavigation = this.refreshNavigation.bind(this);

    // Material type filter methods
    this.initializeMaterialTypeDropdown =
      this.initializeMaterialTypeDropdown.bind(this);
    this.toggleMaterialTypeDropdown =
      this.toggleMaterialTypeDropdown.bind(this);
    this.filterProductsByMaterialType =
      this.filterProductsByMaterialType.bind(this);
    this.closeMaterialTypeDropdown = this.closeMaterialTypeDropdown.bind(this);
  }

  /**
   * Initializes department filters by generating chips
   * @returns {void}
   */
  initializeDepartmentFilters() {
    this.generateDepartmentChips();
    this.initializeMaterialTypeDropdown();
  }

  /**
   * Extracts unique material types from available products
   * @returns {Array<{value: string, label: string}>} Array of material type options
   */
  extractMaterialTypesFromProducts() {
    const materialTypes = new Set();

    // Focus ONLY on visible materials in DOM - this is what matters to the user
    const visibleMaterialItems = document.querySelectorAll(
      '.material-item:not([style*="none"])'
    );

    if (visibleMaterialItems.length === 0) {
      // Fallback: if no materials visible yet, check all products data
      if (window.courseData.products_Materials
       && window.courseData.products_Materials.length > 0) {
        window.courseData.products_Materials.forEach((product, index) => {
          if (product.material_type && product.material_type.trim()) {
            materialTypes.add(product.material_type.trim());
          }
        });
      }
    } else {
      // Primary approach: Only extract from currently visible materials
      visibleMaterialItems.forEach((materialItem, index) => {
        /** @type {HTMLElement} */
        const element = /** @type {HTMLElement} */ (materialItem);
        const product = this.getProductFromMaterialItem(element);

        if (product) {
          const materialType = /** @type {any} */ (product).material_type;
          if (materialType && materialType.trim()) {
            materialTypes.add(materialType.trim());
          }
        }
      });
    }

    // Convert to array and sort with preferred order
    const typeArray = Array.from(materialTypes);
    const preferredOrder = ['REQUIRED', 'RECOMMENDED', 'CHOICE'];

    const sortedTypes = typeArray.sort((a, b) => {
      const aIndex = preferredOrder.indexOf(a.toUpperCase());
      const bIndex = preferredOrder.indexOf(b.toUpperCase());

      // If both are in preferred order, sort by their index
      if (aIndex !== -1 && bIndex !== -1) {
        return aIndex - bIndex;
      }

      // If only one is in preferred order, prioritize it
      if (aIndex !== -1) return -1;
      if (bIndex !== -1) return 1;

      // If neither is in preferred order, sort alphabetically
      return a.localeCompare(b);
    });

    // Convert to option objects
    const options = sortedTypes.map(type => ({
      value: type.toLowerCase(),
      label: this.formatMaterialTypeLabel(type),
    }));

    return options;
  }

  /**
   * Formats material type for display
   * @param {string} type - Raw material type
   * @returns {string} Formatted label
   */
  formatMaterialTypeLabel(type) {
    // Convert to title case for better display
    return type
      .toLowerCase()
      .split(' ')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  }

  /**
   * Initializes the material type dropdown functionality
   * @returns {void}
   */
  initializeMaterialTypeDropdown() {
    // Skip initialization if our custom dropdown already exists
    const customDropdown = document.getElementById('materials-dropdown-menu');
    if (customDropdown) {
      return;
    }

    const dropdownBtn = document.getElementById('all-materials-btn');
    const dropdownContainer = document.querySelector('.all-materials-dropdown');

    if (!dropdownBtn || !dropdownContainer) {
      return;
    }

    // Remove existing dropdown menu if present
    const existingDropdown = dropdownContainer.querySelector(
      '.material-type-dropdown-menu'
    );
    if (existingDropdown) {
      existingDropdown.remove();
    }

    // Create dropdown menu
    const dropdownMenu = document.createElement('div');
    dropdownMenu.className = 'material-type-dropdown-menu';
    dropdownMenu.style.display = 'none';

    // Extract unique material types from products
    const materialTypes = this.extractMaterialTypesFromProducts();

    const options = [
      { value: 'all', label: 'All Materials' },
      ...materialTypes,
    ];

    options.forEach((option, index) => {
      const menuItem = document.createElement('div');
      menuItem.className = 'dropdown-menu-item';
      menuItem.textContent = option.label;
      menuItem.onclick = e => {
        e.preventDefault();
        e.stopPropagation();

        this.filterProductsByMaterialType(option.value, option.label);
        this.closeMaterialTypeDropdown();
      };
      dropdownMenu.appendChild(menuItem);
    });

    dropdownContainer.appendChild(dropdownMenu);

    // Add click event to button
    dropdownBtn.onclick = e => {
      e.stopPropagation();
      this.toggleMaterialTypeDropdown();
    };

    // Close dropdown when clicking outside
    document.addEventListener('click', e => {
      if (!dropdownContainer.contains(/** @type {Node} */ (e.target))) {
        this.closeMaterialTypeDropdown();
      }
    });
  }

  /**
   * Toggles the material type dropdown visibility
   * @returns {void}
   */
  toggleMaterialTypeDropdown() {
    const dropdownMenu = document.querySelector('.material-type-dropdown-menu');
    if (!dropdownMenu) return;

    this.dropdownOpen = !this.dropdownOpen;
    dropdownMenu.style.display = this.dropdownOpen ? 'block' : 'none';

    // Update chevron icon direction
    const chevronIcon = document.querySelector(
      '#all-materials-btn .chevron-down-icon'
    );
    if (chevronIcon) {
      chevronIcon.style.transform = this.dropdownOpen
        ? 'rotate(180deg)'
        : 'rotate(0deg)';
    }
  }

  /**
   * Closes the material type dropdown
   * @returns {void}
   */
  closeMaterialTypeDropdown() {
    const dropdownMenu = document.querySelector('.material-type-dropdown-menu');
    if (!dropdownMenu) return;

    this.dropdownOpen = false;
    dropdownMenu.style.display = 'none';

    // Reset chevron icon
    const chevronIcon = document.querySelector(
      '#all-materials-btn .chevron-down-icon'
    );
    if (chevronIcon) {
      chevronIcon.style.transform = 'rotate(0deg)';
    }
  }

  /**
   * Filters products by material type
   * @param {string} materialType - Material type to filter by ('all', 'required', 'recommended', 'choice')
   * @param {string} label - Display label for the filter
   * @returns {void}
   */
  filterProductsByMaterialType(materialType, label) {
    this.currentMaterialTypeFilter = materialType;

    // Update button text
    const buttonText = document.getElementById('all-materials-text');
    if (buttonText) {
      buttonText.textContent = label;
    }

    // Apply material type filter to all materials
    const allMaterialItems = document.querySelectorAll('.material-item');

    allMaterialItems.forEach((materialItem, index) => {
      /** @type {HTMLElement} */
      const element = /** @type {HTMLElement} */ (materialItem);
      const shouldShow = this.shouldShowMaterialByType(element, materialType);

      if (shouldShow) {
        element.classList.remove('filtering-out');
        element.classList.add('filtering-in');
        // Reset any inline display: none from previous filtering
        if (element.style.display === 'none') {
          element.style.display = '';
        }
      } else {
        element.classList.remove('filtering-in');
        element.classList.add('filtering-out');
        // Hide after animation completes
        setTimeout(() => {
          if (element.classList.contains('filtering-out')) {
            element.style.display = 'none';
          }
        }, 300); // Match CSS transition duration
      }
    });

    // Update course sections visibility
    this.updateCourseSectionsVisibility();

    // Update materials count
    this.updateMaterialsCount();

    // Dispatch custom event
    this.dispatchNavigationChangeEvent('materialTypeFilter', {
      materialType,
      label,
    });
  }

  /**
   * Determines if a material item should be shown based on material type filter
   * @param {HTMLElement} materialItem - Material item element
   * @param {string} materialType - Current filter ('all', 'required', 'recommended', 'choice')
   * @returns {boolean} Whether the item should be shown
   */
  shouldShowMaterialByType(materialItem, materialType) {
    if (materialType === 'all') {
      return true;
    }

    // Get the product data from the material item
    const product = this.getProductFromMaterialItem(materialItem);
    if (!product) {
      return false;
    }

    /** @type {any} */
    const productData = product;

    // Compare with actual material_type from product data
    if (!productData.material_type || !productData.material_type.trim()) {
      return false; // No material type defined
    }

    const productMaterialType = productData.material_type.trim().toLowerCase();
    const filterType = materialType.toLowerCase();
    const matches = productMaterialType === filterType;

    return matches;
  }

  /**
   * Gets the product data associated with a material item DOM element
   * @param {HTMLElement} materialItem - Material item element
   * @returns {Object|null} Product data or null if not found
   */
  getProductFromMaterialItem(materialItem) {
    if (!window.courseData.products_Materials || window.courseData.products_Materials.length === 0) return null;

    // Try to find product by title
    const titleElement = materialItem.querySelector('.material-title');
    if (!titleElement) return null;

    const productTitle = titleElement.textContent.trim();

    // Find the product in our products array by matching title
    let matchedProduct = window.courseData.products_Materials.find(
      product => product.title && product.title.trim() === productTitle
    );

    // If no exact match, try partial matching (fallback)
    if (!matchedProduct) {
      matchedProduct = window.courseData.products_Materials.find(product => {
        if (!product.title) return false;
        const normalizedProductTitle = product.title.trim().toLowerCase();
        const normalizedSearchTitle = productTitle.toLowerCase();
        return (
          normalizedProductTitle.includes(normalizedSearchTitle) ||
          normalizedSearchTitle.includes(normalizedProductTitle)
        );
      });
    }

    return matchedProduct || null;
  }

  /**
   * Updates course sections visibility based on visible materials
   * @returns {void}
   */
  updateCourseSectionsVisibility() {
    document.querySelectorAll('.course-section').forEach(section => {
      const visibleMaterials = section.querySelectorAll(
        '.material-item:not([style*="none"]):not(.filtering-out)'
      );
      const courseCard = section.querySelector('.course-card');
      const adoptionCard = section.querySelector('.course-card.adoption-card');

      if (visibleMaterials.length === 0) {
        // Hide the course card
        if (courseCard) {
          courseCard.style.display = 'none';
          courseCard.style.visibility = 'hidden';          
        }
        if(adoptionCard){
          if (adoptionCard.style.display === 'none') {
            adoptionCard.style.display = '';
          }
          if (adoptionCard.style.visibility === 'hidden') {
            adoptionCard.style.visibility = 'visible';
          }
        }
      } else {
        // Show the course card - reset any previous inline styles
        if (courseCard) {
          if (courseCard.style.display === 'none') {
            courseCard.style.display = '';
          }
          if (courseCard.style.visibility === 'hidden') {
            courseCard.style.visibility = 'visible';
          }
        }
      }
    });
  }

  /**
   * Generates department filter chips based on selected filters
   * @returns {void}
   */
  async generateDepartmentChips() {
    try {
      const chipsContainer = document.getElementById('department-filter-chips');
      const navPanel = document.getElementById('right-panel-nav');
      if (!chipsContainer || !navPanel) {
        return;
      }

      const departments = new Set(['All']); // Always include "All"

      this.selectedFilters.forEach(
        /** @param {any} filter */ filter => {
          if (filter.selectedDepartment) {
            departments.add(filter.selectedDepartment.trim());
          }
        }
      );
      // Only show navigation if we have more than 1 course added (as per feedback)
      const hasMultipleCourses = this.selectedFilters.length > 1;
      const hasVisibleProducts =
        document.querySelectorAll('.course-section .material-item').length > 0;

      if (
        hasMultipleCourses &&
        (window.courseData.products_Materials.length > 0 || hasVisibleProducts)
      ) {
        navPanel.style.display = 'flex';
        
        // Update mobile course header when navigation is shown
        this.updateMobileCourseHeader();

        // Create chips - simplified
        chipsContainer.innerHTML = '';
        Array.from(departments).forEach(
          (
            /** @param {string} dept */ dept,
            /** @param {number} index */ index
          ) => {
            const button = document.createElement('button');
            button.className =
              'department-chip' + (index === 0 ? ' active' : '');
            button.setAttribute(
              'data-department',
              dept === 'All' ? 'all' : dept.toLowerCase()
            );
            button.textContent = dept;
            button.onclick = () =>
              this.filterProductsByDepartment(
                button.getAttribute('data-department') || 'all'
              );
            chipsContainer.appendChild(button);
          }
        );
      } else {
        navPanel.style.display = 'none';
        // Always clear chips when nav is hidden; otherwise a prior multi-course
        // flow leaves stale department pills when switching to student ID / single-course.
        chipsContainer.innerHTML = '';
      }
    } catch (error) {
      console.warn('Error generating department chips:', error);
    }
  }

  /**
   * Filters products by selected department
   * @param {string} department - Department to filter by
   * @returns {void}
   */
  filterProductsByDepartment(department) {
    // Update chip active states
    document.querySelectorAll('.department-chip').forEach(
      /** @param {any} chip */ chip => {
        chip.classList.toggle(
          'active',
          chip.getAttribute('data-department') === department
        );
      }
    );

    // Show/hide course sections based on department filter
    document.querySelectorAll('.course-section').forEach(
      /** @param {any} section */ section => {
        const title = section.querySelector(
          '.department-title .department-name'
        );
        const sectionDepartmentFilter =
          section.getAttribute('data-department-filter')?.toLowerCase().trim() ||
          '';
        
        let shouldShow = false;
        
        if (department === 'all') {
          // Show all sections when "All" is selected
          shouldShow = true;
        } else if (sectionDepartmentFilter) {
          // Preserve department filtering regardless of displayed title text.
          const departmentName = department.toLowerCase().trim();
          shouldShow = sectionDepartmentFilter === departmentName;
        } else if (title) {
          // Match department name (case-insensitive)
          const titleText = title.textContent.toLowerCase().trim();
          const departmentName = department.toLowerCase().trim();
          shouldShow = titleText === departmentName;
        }
        // Apply visibility with proper display style
        if (shouldShow) {
          section.style.display = 'flex';
          section.style.visibility = 'visible';
        } else {
          section.style.display = 'none';
          section.style.visibility = 'hidden';
        }
      }
    );

    // Re-apply material type filter to visible sections
    this.applyCurrentMaterialTypeFilter();

    // Update materials count after filtering
    this.updateMaterialsCount();

    // Dispatch custom event for external listeners
    this.dispatchNavigationChangeEvent('filter', { department });
  }

  /**
   * Applies the current material type filter to all materials
   * @returns {void}
   */
  applyCurrentMaterialTypeFilter() {
    if (this.currentMaterialTypeFilter === 'all') {
      // Show all materials in visible sections with smooth transition
      document
        .querySelectorAll('.course-section:not([style*="none"]) .material-item')
        .forEach(materialItem => {
          /** @type {HTMLElement} */
          const element = /** @type {HTMLElement} */ (materialItem);
          element.classList.remove('filtering-out');
          element.classList.add('filtering-in');
          // Reset any inline display: none from previous filtering
          if (element.style.display === 'none') {
            element.style.display = '';
          }
        });
    } else {
      // Apply current material type filter with smooth transitions
      document
        .querySelectorAll('.course-section:not([style*="none"]) .material-item')
        .forEach(materialItem => {
          /** @type {HTMLElement} */
          const element = /** @type {HTMLElement} */ (materialItem);
          const shouldShow = this.shouldShowMaterialByType(
            element,
            this.currentMaterialTypeFilter
          );

          if (shouldShow) {
            element.classList.remove('filtering-out');
            element.classList.add('filtering-in');
            // Reset any inline display: none from previous filtering
            if (element.style.display === 'none') {
              element.style.display = '';
            }
          } else {
            element.classList.remove('filtering-in');
            element.classList.add('filtering-out');
            setTimeout(() => {
              if (element.classList.contains('filtering-out')) {
                element.style.display = 'none';
              }
            }, 300);
          }
        });
    }

    // Update course sections visibility after material filtering
    this.updateCourseSectionsVisibility();
  }

  /**
   * Updates the count of visible materials
   * @returns {void}
   */
  updateMaterialsCount() {
    try {
      const materialsCount = document.getElementById('materials-count');
      if (!materialsCount) {
        return;
      }

      // Count materials that are visible in visible sections
      const visibleProducts = document.querySelectorAll(
        '.course-section:not([style*="none"]) .material-item:not([style*="none"])'
      );
      const count = visibleProducts.length;

      if (count > 0) {
        // Determine label based on current filter
        let label = 'material';
        if (this.currentMaterialTypeFilter !== 'all') {
          // Format the filter name for display
          label = `${this.formatMaterialTypeLabel(
            this.currentMaterialTypeFilter
          )} material`.toLowerCase();
        }

        materialsCount.textContent = `${count} ${label}${
          count !== 1 ? 's' : ''
        }`;
      } else {
        materialsCount.textContent = '';
      }
    } catch (error) {
      console.warn('Error updating materials count:', error);
    }
  }

  /**
   * Updates the campus and term display in the header
   * @returns {void}
   */
  updateCampusTermDisplay() {
    try {
      const campusDisplay = document.getElementById('selected-campus-display');
      const campusTermBar = document.getElementById('campus-term-info');
      if (!campusDisplay || !campusTermBar) {
        return;
      }

      const hasVisibleProducts =
        document.querySelectorAll('.course-section .material-item').length > 0;

      // Show campus|term whenever the active filter includes them — do not wait for
      // products[] or DOM material items (those load async after campus/term selection).
      if (this.selectedFilters.length === 0) {
        campusDisplay.textContent = '';
        campusTermBar.style.display = 'none';
        return;
      }

      const firstFilter = this.selectedFilters[0];
      const campus = firstFilter?.selectedCampus || '';
      const term = firstFilter?.selectedTerm || '';
      const termId = firstFilter?.selectedTermId || '';

      const termName = window.courseData?.ddcs_term?.find(t => t.term_id === termId)?.name || term;
      if (campus && term) {
        campusDisplay.textContent = `${campus} | ${termName}`;
        campusTermBar.style.display = 'flex';
      } else {
        campusDisplay.textContent = '';
        campusTermBar.style.display = 'none';
      }
    } catch (error) {
      console.warn('Error updating campus term display:', error);
    }
  }

  /**
   * Hides navigation panels (useful for empty states)
   * @returns {void}
   */
  hideNavigationPanels() {
    const navPanel = document.getElementById('right-panel-nav');
    const campusTermBar = document.getElementById('campus-term-info');
    const chipsContainer = document.getElementById('department-filter-chips');

    if (navPanel) navPanel.style.display = 'none';
    if (campusTermBar) campusTermBar.style.display = 'none';
    if (chipsContainer) chipsContainer.innerHTML = '';
  }

  /**
   * Updates the selected filters reference (for external updates)
   * @param {Array<any>} newSelectedFilters - New selected filters array
   * @returns {void}
   */
  updateSelectedFilters(newSelectedFilters) {
    this.selectedFilters = newSelectedFilters;
  }

  /**
   * Updates the products reference (for external updates)
   * @param {Array<any>} newProducts - New products array
   * @returns {void}
   */
  updateProducts(newProducts) {
    this.products = newProducts;
    // Re-initialize dropdown with new product data
    setTimeout(() => {
      this.initializeMaterialTypeDropdown();
    }, 100);
  }

  /**
   * Refreshes all navigation components
   * Convenience method that updates everything
   * @returns {void}
   */
  refreshNavigation() {
    this.generateDepartmentChips();
    // Re-initialize dropdown to pick up any new material types
    this.initializeMaterialTypeDropdown();
    this.updateCampusTermDisplay();
    this.updateMaterialsCount();
  }

  /**
   * Gets the current material type filter
   * @returns {string} Current material type filter
   */
  getCurrentMaterialTypeFilter() {
    return this.currentMaterialTypeFilter;
  }

  /**
   * Resets all filters to default state
   * @returns {void}
   */
  resetAllFilters() {
    // Reset department filter
    this.resetDepartmentFilter();

    // Clean up any filtering classes before reset
    this.cleanupFilteringClasses();

    // Reset material type filter
    this.currentMaterialTypeFilter = 'all';
    this.filterProductsByMaterialType('all', 'All Materials');
  }

  /**
   * Cleans up filtering CSS classes from all material items
   * @returns {void}
   */
  cleanupFilteringClasses() {
    document.querySelectorAll('.material-item').forEach(item => {
      item.classList.remove('filtering-in', 'filtering-out');
      // Reset any inline display: none from previous filtering
      if (item.style.display === 'none') {
        item.style.display = '';
      }
    });
  }

  /**
   * Initializes the navigation UI on DOM ready
   * @returns {void}
   */
  initializeOnDOMReady() {
    this.hideNavigationPanels();

    if (this.selectedFilters.length > 0) {
      this.initializeDepartmentFilters();
      this.updateCampusTermDisplay();
    }

    // Listen for materials loaded event to update dropdown options
    document.addEventListener('materialsLoaded', () => {
      setTimeout(() => {
        this.initializeMaterialTypeDropdown();
      }, 50);
    });
  }

  /**
   * Gets the currently active department filter
   * @returns {string|null} Active department or null
   */
  getActiveDepartment() {
    const activeChip = document.querySelector('.department-chip.active');
    return activeChip ? activeChip.getAttribute('data-department') : null;
  }

  /**
   * Sets the active department filter
   * @param {string} department - Department to set as active
   * @returns {void}
   */
  setActiveDepartment(department) {
    this.filterProductsByDepartment(department);
  }

  /**
   * Gets statistics about visible products
   * @returns {Object} Statistics object with counts and visibility info
   */
  getNavigationStats() {
    const visibleProducts = document.querySelectorAll(
      '.course-section:not([style*="none"]) .material-item'
    );
    const totalProducts = document.querySelectorAll(
      '.course-section .material-item'
    );
    const activeDepartment = this.getActiveDepartment();

    return {
      visibleProductsCount: visibleProducts.length,
      totalProductsCount: totalProducts.length,
      activeDepartment,
      hasActiveFilters: this.selectedFilters.length > 0,
      isNavigationVisible:
        document.getElementById('right-panel-nav')?.style.display === 'flex',
    };
  }

  /**
   * Dispatches a custom event when navigation changes
   * @private
   * @param {string} type - Type of navigation change
   * @param {Object} data - Additional data about the change
   * @returns {void}
   */
  dispatchNavigationChangeEvent(type, data) {
    const event = new CustomEvent('navigation:changed', {
      detail: {
        type,
        data,
        stats: this.getNavigationStats(),
      },
    });
    document.dispatchEvent(event);
  }

  /**
   * Resets all filters to show all departments
   * @returns {void}
   */
  resetDepartmentFilter() {
    this.filterProductsByDepartment('all');
  }

  /**
   * Updates the mobile course header with current campus/term and materials count
   * @returns {void}
   */
  updateMobileCourseHeader() {
    try {
      const mobileTitle = document.getElementById('mobile-course-title');
      const mobileSubtitle = document.getElementById('mobile-course-subtitle');
      
      if (!mobileTitle || !mobileSubtitle) {
        return;
      }

      // Get campus-term information from multiple sources
      let campusTermText = '';
      
      // Try to get from campus-term display first
      const campusTermDisplay = document.getElementById('selected-campus-display');
      if (campusTermDisplay && campusTermDisplay.textContent && campusTermDisplay.textContent.trim()) {
        campusTermText = campusTermDisplay.textContent.trim();
      } else {
        // Try to get from selected-campus-term-text as fallback
        const selectedCampusTermText = document.getElementById('selected-campus-term-text');
        if (selectedCampusTermText && selectedCampusTermText.textContent && selectedCampusTermText.textContent.trim()) {
          campusTermText = selectedCampusTermText.textContent.trim();
        }
      }

      // Calculate total required materials count
      const visibleProducts = document.querySelectorAll(
        '.course-section:not([style*="none"]) .material-item:not([style*="none"])'
      );
      const totalRequiredCount = Array.from(visibleProducts).filter(
        item => item.getAttribute('data-material-type') === 'Required'
      ).length;

      // Update mobile header
      mobileTitle.textContent = campusTermText || 'Course Materials';
      mobileSubtitle.textContent = `(${totalRequiredCount} required materials)`;
    } catch (error) {
      console.warn('Error updating mobile course header:', error);
    }
  }
}

// Export for use in other modules and global scope
window.NavigationUIManager = NavigationUIManager;

// Create namespace if it doesn't exist
if (!window.CourseMatSystem) {
  window.CourseMatSystem = {};
}
window.CourseMatSystem.NavigationUIManager = NavigationUIManager;
